import clsx from 'clsx'
import Button from 'components/Button'
import confirm from 'utils/confirm'
import Typography from 'components/Typography'
import ProgressBar from 'components/ProgressBar'
import iocnInfinity from 'assets/svg/infinity.svg'
import AnalyticsService from 'utils/analytics'
import { t } from 'utils/localization'
import { parseWord } from 'utils/helpers'
import { useProfile } from 'utils/hooks/useContext'
import { IPlan, TPlanPrice, TUnit } from 'utils/types'
import './styles.scss'

export const renderParamValue = (value: number, title: string, unit: TUnit) => {
  let textComponent = null
  let progressComponent = null

  if (value === -1) {
    textComponent = <img alt='infinity' src={iocnInfinity} />
    progressComponent = <ProgressBar percent={100} />
  } else {
    let unitText = ''

    switch (unit) {
      case 'users':
        unitText = parseWord(value, ['человек', 'человек', 'человека'], unit)
        progressComponent = <ProgressBar percent={(value * 100) / 20} />
        break
      case 'rooms':
        unitText = parseWord(value, ['комнат', 'комната', 'комнаты'], unit)
        progressComponent = <ProgressBar percent={(value * 100) / 20} />
        break
      case 'minutes':
        unitText = parseWord(value, ['минут', 'минута', 'минуты'], unit)
        progressComponent = <ProgressBar percent={(value * 100) / 50} />
        break
      default:
        break
    }

    textComponent = (
      <Typography size='xs' weight='semi' color='primary'>
        {unitText}
      </Typography>
    )
  }

  return (
    <>
      <div className='b-tariff__charachteristic'>
        <Typography size='xs'>{title}</Typography>
        {textComponent}
      </div>
      {progressComponent}
    </>
  )
}

interface TariffCardProps {
  main?: boolean
  plan: IPlan
  price: TPlanPrice
  onSelect(planId: string): void
  annualSubscription: boolean
}

const TariffCard: React.FC<TariffCardProps> = (props) => {
  const { user } = useProfile()
  const { main, plan, price, onSelect, annualSubscription } = props

  const selectedPlan = user?.cloudData.plan
  const isSelectedPlan = selectedPlan?.id.includes(plan.id)
  const isFirstPurchase =
    !user?.authData.planIsSelected && selectedPlan?.id === 'free'

  const priceValue = annualSubscription
    ? price.pricePerYear
    : price.pricePerMonth

  const handleSelectPlan = async () => {
    let planId = annualSubscription ? plan.id + 'Annual' : plan.id
    let canChangePlan = true

    if (selectedPlan && !isFirstPurchase) {
      const chooseFreePlan =
        plan.id === 'free' ||
        (selectedPlan.id.includes('pro') && plan.id.includes('pro')) ||
        (selectedPlan.id.includes('indie') && plan.id.includes('indie'))

      if (
        (selectedPlan.id.includes('pro') && plan.id.includes('pro')) ||
        (selectedPlan.id.includes('pro') && plan.id.includes('free')) ||
        (selectedPlan.id.includes('pro') && plan.id.includes('indie')) ||
        (selectedPlan.id.includes('indie') && plan.id.includes('indie')) ||
        (selectedPlan.id.includes('indie') && plan.id.includes('free'))
      ) {
        if (selectedPlan.id === plan.id) {
          planId = 'free'
        }
        canChangePlan =
          (await confirm(
            t('plan-downgrade-confirmation', {
              plan: t(chooseFreePlan ? 'price-free' : price.title)
            })
          )) === 'true'
      }
    }

    if (canChangePlan) {
      onSelect(planId)
    }

    if (isSelectedPlan && !isFirstPurchase) {
      AnalyticsService.track('CancelSubscription')
    } else {
      AnalyticsService.track(`select-plan-${plan.id}`)
    }
  }

  return (
    <div className={clsx('b-tariff', main && 'b-tariff--main')}>
      <Typography className='b-tariff__type'>{t(price.title)}</Typography>

      <Typography className='b-tariff__price'>
        <span className='b-tariff__currency'>$</span>
        {priceValue}
        <span className='b-tariff__frequency'>/ {t('per-month')}</span>
      </Typography>

      {renderParamValue(plan.timeLimit, t('session-duration'), 'minutes')}

      {renderParamValue(plan.ccuLimit, t('avatar-count'), 'users')}

      {renderParamValue(plan.roomLimit, t('room-count'), 'rooms')}

      {main && (
        <Typography size='xs' className='b-tariff__offer'>
          {t('get-free-month', { price: priceValue })}
        </Typography>
      )}

      <Button
        variant={plan.id === 'free' || isSelectedPlan ? 'outline' : 'primary'}
        onClick={handleSelectPlan}
        disabled={
          !isFirstPurchase && plan.id === 'free' && selectedPlan?.id === 'free'
        }
        fullWidth
      >
        {isSelectedPlan && !isFirstPurchase ? (
          t('cancel-subscription')
        ) : (
          <>
            {plan.id === 'free' && t('begin')}
            {plan.id === 'indie' && t('try-for-free')}
            {plan.id === 'pro' && t('connect')}
          </>
        )}
      </Button>
    </div>
  )
}

export default TariffCard
